<template>
  <div class="intake-assist-page" dir="rtl">
    <h1 class="page-title">להשלים ראיון בטלפון</h1>

    <div class="tabs-bar">
      <button
        v-for="t in tabs"
        :key="t.scope"
        class="tab"
        :class="{ active: scope === t.scope }"
        :disabled="is_loading && scope === t.scope"
        @click="setScope(t.scope)"
      >{{ t.label }}</button>
    </div>

    <div v-if="is_loading && !tasks.length" class="loading-wrap">
      <div class="loading"></div>
    </div>

    <p v-else-if="!tasks.length" class="empty-state">{{ emptyText }}</p>

    <div v-else class="cards">
      <div v-for="task in tasks" :key="task.id" class="task-card" :class="{ 'is-due': scope === 'open' && task.due }">
        <!-- Row 1: who + reason -->
        <div class="row row-head">
          <a v-if="task.applicant" class="applicant-name" @click.prevent="openApplicant(task)">{{ applicantName(task) }}</a>
          <span v-else class="applicant-name">—</span>
          <a v-if="task.applicant && task.applicant.phone" class="phone" :href="'tel:' + task.applicant.phone" dir="ltr">{{ task.applicant.phone }}</a>
          <span class="reason-tag" :class="'reason-' + task.reason">{{ reasonLabel(task) }}</span>
        </div>

        <!-- Row 2: what is missing + offer -->
        <div class="row row-missing">
          <strong v-if="task.pending_question_label">חסר: {{ task.pending_question_label }}</strong>
          <span v-if="task.offer" class="offer-line">{{ offerLine(task.offer) }}</span>
        </div>

        <!-- Row 3: summary -->
        <div v-if="task.summary" class="row row-summary">
          <p class="summary" :class="{ collapsed: !expanded[task.id] }">{{ task.summary }}</p>
          <a v-if="isLongSummary(task.summary)" class="more-toggle" @click.prevent="toggleExpanded(task.id)">{{ expanded[task.id] ? 'פחות' : 'עוד' }}</a>
        </div>

        <!-- Row 4: timing / attempts / owner / outcome -->
        <div class="row row-meta">
          <span v-if="scope !== 'done' && task.outcome === 'callback' && task.callback_at">לחזור: {{ timeLabel(task.callback_at) }}</span>
          <span v-else-if="scope !== 'done' && task.due_at">{{ timeLabel(task.due_at) }}</span>
          <span v-if="task.attempts > 0">ניסיון {{ task.attempts + (scope === 'done' ? 0 : 1) }}</span>
          <span v-if="scope !== 'open' && task.owner">{{ task.owner.name }}</span>
          <span v-if="scope === 'done' && task.outcome" class="outcome">{{ outcomeLabel(task.outcome) }}</span>
          <span v-if="scope === 'done'">{{ timeLabel(task.updated_at) }}</span>
          <span v-if="scope === 'done' && task.outcome_note" class="outcome-note">{{ task.outcome_note }}</span>
        </div>

        <!-- Actions: open tab -->
        <div v-if="scope === 'open'" class="row row-actions">
          <button class="button is-small is-primary" :class="{ 'is-loading': busy[task.id] }" :disabled="!!busy[task.id]" @click="claim(task)">התחל שיחה</button>
          <button class="button is-small is-white" data-test="dismiss-open" :disabled="!!busy[task.id]" @click="toggleDismiss(task.id)">אין צורך לטפל</button>
          <button v-if="task.conversation_id" class="button is-small is-light" :disabled="!!busy[task.id]" @click="openConversation(task)">שיחה</button>
        </div>

        <!-- Actions: mine tab -->
        <div v-if="scope === 'mine'" class="row row-actions-mine">
          <input
            v-model="notes[task.id]"
            class="input is-small note-input"
            type="text"
            maxlength="500"
            placeholder="הערה (לא חובה)"
            :disabled="!!busy[task.id]"
          >
          <div class="action-buttons">
            <button class="button is-small is-success" :disabled="!!busy[task.id]" @click="recordOutcome(task, 'completed')">הושלם</button>
            <button class="button is-small is-warning" :disabled="!!busy[task.id]" @click="recordOutcome(task, 'no_answer')">לא ענה/תה</button>
            <button class="button is-small is-info is-light" :disabled="!!busy[task.id]" @click="toggleCallback(task.id)">לחזור מאוחר יותר</button>
            <button class="button is-small is-danger is-light" :disabled="!!busy[task.id]" @click="recordOutcome(task, 'declined')">לא מעוניין/ת</button>
            <button class="button is-small is-white" :disabled="!!busy[task.id]" @click="release(task)">שחרור</button>
            <button class="button is-small is-white" data-test="dismiss-open" :disabled="!!busy[task.id]" @click="toggleDismiss(task.id)">אין צורך לטפל</button>
            <button v-if="task.conversation_id" class="button is-small is-light" @click="openConversation(task)">שיחה</button>
          </div>
          <div v-if="callbackOpen[task.id]" class="callback-row">
            <label :for="'callback-' + task.id">מתי לחזור:</label>
            <input
              :id="'callback-' + task.id"
              v-model="callbackAt[task.id]"
              class="input is-small callback-input"
              type="datetime-local"
              required
              :disabled="!!busy[task.id]"
            >
            <button class="button is-small is-info" :disabled="!!busy[task.id] || !callbackAt[task.id]" @click="recordOutcome(task, 'callback')">שמירה</button>
          </div>
          <p v-if="busy[task.id]" class="busy-line">שומר...</p>
        </div>

        <!-- Dismiss confirmation (open + mine): close without a call -->
        <div v-if="scope !== 'done' && dismissOpen[task.id]" class="dismiss-row" data-test="dismiss-row">
          <label :for="'dismiss-' + task.id">לסגור בלי שיחה?</label>
          <input
            :id="'dismiss-' + task.id"
            v-model="dismissNotes[task.id]"
            class="input is-small note-input"
            type="text"
            maxlength="500"
            placeholder="הערה (לא חובה)"
            data-test="dismiss-note"
            :disabled="!!busy[task.id]"
            @keyup.enter="dismiss(task)"
          >
          <button class="button is-small is-dark" data-test="dismiss-confirm" :class="{ 'is-loading': busy[task.id] }" :disabled="!!busy[task.id]" @click="dismiss(task)">סגירה</button>
          <button class="button is-small is-white" :disabled="!!busy[task.id]" @click="toggleDismiss(task.id)">ביטול</button>
        </div>
      </div>

      <div v-if="totalPages > 1" class="pager">
        <button class="button is-small" :disabled="page <= 1 || is_loading" @click="goPage(page - 1)">הקודם</button>
        <span>{{ page }} / {{ totalPages }}</span>
        <button class="button is-small" :disabled="page >= totalPages || is_loading" @click="goPage(page + 1)">הבא</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'

const REASON_LABELS = {
  human_request: 'ביקש/ה נציג/ה',
  abandoned: 'השיחה נעצרה'
}
// abandoned + bot_no_reply_24h: the candidate wrote last and is waiting on us.
const BOT_STALL_TRIGGER = 'bot_no_reply_24h'
const BOT_STALL_LABEL = 'ממתין/ה לתשובה'
const OUTCOME_LABELS = {
  completed: 'הושלם',
  no_answer: 'לא ענה/תה',
  callback: 'לחזור מאוחר יותר',
  declined: 'לא מעוניין/ת',
  not_needed: 'אין צורך לטפל'
}
const EMPTY_TEXTS = {
  open: 'אין שיחות להשלמה כרגע',
  mine: 'לא לקחת שיחות',
  done: 'עדיין לא טופלו שיחות'
}
const CONVERSATION_PANEL_BASE = 'https://chatbot.test.panel.hasama.online'

export default {
  name: 'intake-assist-tasks-page',
  data () {
    return {
      tabs: [
        { scope: 'open', label: 'לטיפול' },
        { scope: 'mine', label: 'שלי' },
        { scope: 'done', label: 'טופל' }
      ],
      scope: 'open',
      tasks: [],
      total: 0,
      page: 1,
      per: 50,
      is_loading: false,
      fetchSeq: 0,
      busy: {},
      notes: {},
      callbackOpen: {},
      callbackAt: {},
      dismissOpen: {},
      dismissNotes: {},
      expanded: {}
    }
  },
  computed: {
    emptyText () {
      return EMPTY_TEXTS[this.scope]
    },
    totalPages () {
      return Math.max(1, Math.ceil(this.total / this.per))
    }
  },
  created () {
    this.fetchTasks()
  },
  methods: {
    setScope (scope) {
      if (this.scope === scope) {
        this.fetchTasks()
        return
      }
      this.scope = scope
      this.page = 1
      this.tasks = []
      this.total = 0
      this.fetchTasks()
    },
    goPage (page) {
      this.page = page
      this.fetchTasks()
    },
    fetchTasks () {
      const seq = ++this.fetchSeq
      const scope = this.scope
      this.is_loading = true
      return this.$network_utils.getIntakeAssistTasks({ scope, page: this.page, per: this.per })
        .then(response => {
          if (seq !== this.fetchSeq) return
          this.tasks = (response && response.tasks) || []
          this.total = (response && response.total) || 0
        })
        .catch(() => {
          if (seq !== this.fetchSeq) return
          this.toast('שגיאה בטעינת הרשימה', 'error')
        })
        .finally(() => {
          if (seq === this.fetchSeq) this.is_loading = false
        })
    },
    afterChange () {
      this.fetchTasks()
      this.$utils.emit('refresh_counters')
    },
    toast (text, type) {
      this.$utils.emit('show_toast', type ? { text, type } : { text })
    },
    setBusy (id, value) {
      this.$set(this.busy, id, value)
    },
    // 409/410/403 mean the task changed on the server: tell the recruiter and reload.
    handleError (error) {
      const response = error && error.response
      const status = response && response.status
      const data = (response && response.data) || {}
      if (status === 409 && data.error === 'already_claimed') {
        const ownerName = data.owner && data.owner.name ? data.owner.name : 'מישהי אחרת'
        this.toast(`כבר נלקח על ידי ${ownerName}`, 'error')
        this.afterChange()
        return
      }
      if (status === 410) {
        this.toast('השיחה כבר טופלה', 'error')
        this.afterChange()
        return
      }
      if (status === 403) {
        this.toast('השיחה לא אצלך', 'error')
        this.afterChange()
        return
      }
      if (status === 422 && data.error === 'callback_at_required') {
        this.toast('צריך לבחור מתי לחזור', 'error')
        return
      }
      this.toast('הפעולה לא נשמרה, נסו שוב', 'error')
    },
    claim (task) {
      if (this.busy[task.id]) return
      this.setBusy(task.id, true)
      this.$network_utils.claimIntakeAssistTask({ id: task.id })
        .then(() => {
          this.toast('השיחה עברה ל"שלי"')
          this.afterChange()
        })
        .catch(this.handleError)
        .finally(() => this.setBusy(task.id, false))
    },
    release (task) {
      if (this.busy[task.id]) return
      this.setBusy(task.id, true)
      this.$network_utils.releaseIntakeAssistTask({ id: task.id })
        .then(() => {
          this.toast('השיחה חזרה לרשימה')
          this.afterChange()
        })
        .catch(this.handleError)
        .finally(() => this.setBusy(task.id, false))
    },
    toggleCallback (id) {
      this.$set(this.callbackOpen, id, !this.callbackOpen[id])
    },
    recordOutcome (task, outcome) {
      if (this.busy[task.id]) return
      const params = { id: task.id, outcome }
      const note = (this.notes[task.id] || '').trim()
      if (note) params.note = note
      if (outcome === 'callback') {
        const local = this.callbackAt[task.id]
        const when = local ? new Date(local) : null
        if (!when || isNaN(when.getTime())) {
          this.toast('צריך לבחור מתי לחזור', 'error')
          return
        }
        params.callback_at = when.toISOString()
      }
      this.setBusy(task.id, true)
      this.$network_utils.recordIntakeAssistOutcome(params)
        .then(() => {
          this.toast('נשמר')
          this.$delete(this.notes, task.id)
          this.$delete(this.callbackOpen, task.id)
          this.$delete(this.callbackAt, task.id)
          this.afterChange()
        })
        .catch(this.handleError)
        .finally(() => this.setBusy(task.id, false))
    },
    toggleDismiss (id) {
      this.$set(this.dismissOpen, id, !this.dismissOpen[id])
    },
    dismiss (task) {
      if (this.busy[task.id]) return
      const params = { id: task.id }
      const note = (this.dismissNotes[task.id] || '').trim()
      if (note) params.note = note
      this.setBusy(task.id, true)
      this.$network_utils.dismissIntakeAssistTask(params)
        .then(() => {
          this.toast('הפנייה נסגרה - אין צורך לטפל')
          this.$delete(this.dismissOpen, task.id)
          this.$delete(this.dismissNotes, task.id)
          this.afterChange()
        })
        .catch(error => {
          this.$delete(this.dismissOpen, task.id)
          this.handleError(error)
        })
        .finally(() => this.setBusy(task.id, false))
    },
    openConversation (task) {
      if (!task.conversation_id) return
      // Same readonly panel URL as OfferConversationButton.vue.
      const slug = this.$settings.get({ path: 'intake_operation_slug' }) || task.operation
      let url = `${CONVERSATION_PANEL_BASE}?conversation_id=${encodeURIComponent(task.conversation_id)}&readonly=true`
      if (task.applicant && task.applicant.id) url += `&applicant_id=${task.applicant.id}`
      if (slug) url += `&operation=${slug}`
      window.open(url, '_blank')
    },
    openApplicant (task) {
      const applicant = task.applicant
      if (!applicant || !applicant.id) return
      const fullName = applicant.name || ''
      const parts = fullName.split(' ')
      this.createModal({
        designator: 'link-entity-resource-modal',
        resource_type: 'Applicant',
        resource_id: applicant.id,
        component_name: 'ModalApplicant',
        data: {
          resource_type: 'Applicant',
          resource_id: applicant.id,
          first_name: parts[0] || '',
          last_name: parts.slice(1).join(' ') || '',
          full_name: fullName,
          phone_number: applicant.phone || ''
        },
        modal_title: `${fullName} | ${applicant.phone || ''} | ${applicant.id}`,
        modal_width: 1500,
        is_center_modal: true,
        modal_open_action: 'click'
      })
    },
    applicantName (task) {
      return (task.applicant && task.applicant.name) || ('מועמד/ת ' + task.applicant.id)
    },
    reasonLabel (task) {
      if (task.reason === 'abandoned' && task.trigger_reason === BOT_STALL_TRIGGER) return BOT_STALL_LABEL
      return REASON_LABELS[task.reason] || task.reason
    },
    outcomeLabel (outcome) {
      return OUTCOME_LABELS[outcome] || outcome
    },
    offerLine (offer) {
      return [offer.position_title, offer.company_name].filter(Boolean).join(' · ')
    },
    isLongSummary (summary) {
      return !!summary && (summary.length > 160 || summary.indexOf('\n') !== -1)
    },
    toggleExpanded (id) {
      this.$set(this.expanded, id, !this.expanded[id])
    },
    timeLabel (value) {
      if (!value) return ''
      const m = this.$moment(value)
      if (!m.isValid()) return ''
      const now = this.$moment()
      if (m.isSame(now, 'day')) return 'היום ' + m.format('HH:mm')
      if (m.isSame(now.clone().add(1, 'day'), 'day')) return 'מחר ' + m.format('HH:mm')
      if (m.isSame(now.clone().subtract(1, 'day'), 'day')) return 'אתמול ' + m.format('HH:mm')
      return m.format('DD/MM HH:mm')
    },
    ...mapMutations('modals', { createModal: 'create' })
  }
}
</script>

<style scoped>
.intake-assist-page {
  padding: 20px;
  text-align: right;
  max-width: 900px;
}
.page-title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 18px;
  color: #2c3e50;
}
.tabs-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
}
.tab {
  border: 1px solid #cbd5e0;
  background: #fff;
  border-radius: 20px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
}
.tab.active {
  background: #3273dc;
  color: #fff;
  border-color: #3273dc;
}
.empty-state {
  color: #6b7280;
  font-size: 16px;
  padding: 24px 0;
}
.cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.task-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-right: 4px solid #cbd5e0;
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.task-card.is-due {
  border-right-color: #B45309;
}
.row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.applicant-name {
  font-size: 16px;
  font-weight: 700;
}
.phone {
  font-size: 15px;
}
.reason-tag {
  font-size: 12px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 12px;
}
.reason-human_request {
  background: #FDE2E1;
  color: #B42318;
}
.reason-abandoned {
  background: #FEF3C7;
  color: #B45309;
}
.offer-line {
  color: #4b5563;
}
.row-summary {
  align-items: flex-start;
  flex-wrap: nowrap;
}
.summary {
  margin: 0;
  color: #374151;
  white-space: pre-wrap;
  flex: 1;
}
.summary.collapsed {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.more-toggle {
  white-space: nowrap;
  font-size: 13px;
}
.row-meta {
  color: #6b7280;
  font-size: 13px;
}
.outcome {
  font-weight: 700;
  color: #374151;
}
.outcome-note {
  font-style: italic;
}
.row-actions-mine {
  flex-direction: column;
  align-items: stretch;
}
.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.note-input {
  max-width: 420px;
}
.callback-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.callback-input {
  max-width: 220px;
}
.dismiss-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 6px;
  border-top: 1px dashed #e2e8f0;
}
.busy-line {
  color: #6b7280;
  font-size: 12px;
  margin: 0;
}
.pager {
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: center;
  margin-top: 8px;
}
.loading-wrap {
  padding: 24px 0;
}
</style>
